import { useState, useEffect, useCallback } from "react";
import { Globe } from "lucide-react";
import { Button } from "@/components/ui/button";

declare global {
  interface Window {
    google: any;
    googleTranslateElementInit: () => void;
  }
}

const LanguageToggle = () => {
  const [currentLang, setCurrentLang] = useState<"en" | "es">("en");
  const [initialized, setInitialized] = useState(false);

  useEffect(() => {
    // Check if already loaded
    if (document.getElementById("google-translate-script")) return;

    // Create hidden container for Google Translate widget
    const container = document.createElement("div");
    container.id = "google_translate_element";
    container.style.display = "none";
    document.body.appendChild(container);

    // Define init callback
    window.googleTranslateElementInit = () => {
      new window.google.translate.TranslateElement(
        {
          pageLanguage: "en",
          includedLanguages: "en,es",
          autoDisplay: false,
        },
        "google_translate_element"
      );
      setInitialized(true);
    };

    // Load the script
    const script = document.createElement("script");
    script.id = "google-translate-script";
    script.src =
      "//translate.google.com/translate_a/element.js?cb=googleTranslateElementInit";
    script.async = true;
    document.body.appendChild(script);

    return () => {
      // Cleanup not needed — Google Translate persists
    };
  }, []);

  const toggleLanguage = useCallback(() => {
    const targetLang = currentLang === "en" ? "es" : "en";

    // Find the Google Translate select element and change it
    const select = document.querySelector(
      ".goog-te-combo"
    ) as HTMLSelectElement | null;

    if (select) {
      select.value = targetLang;
      select.dispatchEvent(new Event("change"));
      setCurrentLang(targetLang);

      // Notify other components (e.g. video swap)
      window.dispatchEvent(new CustomEvent("languagechange", { detail: targetLang }));
    }
  }, [currentLang]);

  return (
    <Button
      variant="ghost"
      size="sm"
      onClick={toggleLanguage}
      className="notranslate flex items-center gap-1.5 text-foreground/80 hover:text-foreground"
      translate="no"
      aria-label={currentLang === "en" ? "Traducir al español" : "Translate to English"}
    >
      <Globe className="h-4 w-4" />
      <span className="text-xs font-semibold notranslate">
        {currentLang === "en" ? "ES" : "EN"}
      </span>
    </Button>
  );
};

export default LanguageToggle;
